import { ChevronDown } from 'lucide-react';

import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  MARGIN_BOTTOM,
  RADIUS_SCALE,
  SHADOW_SCALE,
  SPACE_X_SCALE,
  SURFACE_PADDING,
  PADDING_X,
  PADDING_Y,
  MARGIN_X,
  TYPE_SCALE,
  SIZE_SCALE,
  FLEX,
} from '@/components/design-system/tokens';
import type { Tables } from '@/integrations/supabase/types';

interface UserMenuToggleButtonProps {
  headerOnLight: boolean;
  isOpen: boolean;
  onToggle: () => void;
  displayName: string;
  uppercaseDisplayName: string;
  avatarInitial: string;
  user: Tables<'profiles'> | null;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
}

export const UserMenuToggleButton = ({
  headerOnLight,
  isOpen,
  onToggle,
  displayName,
  uppercaseDisplayName,
  avatarInitial,
  user,
  t,
}: UserMenuToggleButtonProps) => (
  <button
    onClick={onToggle}
    className={`${TYPE_SCALE.xs} transition-all duration-300 flex items-center ${SPACE_X_SCALE['2']} whitespace-nowrap ${PADDING_X['3']} ${PADDING_Y['1.5']} rounded-full font-medium focus-visible:outline-none ${
      headerOnLight
        ? `${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']}/70`
        : `text-white/80 hover:text-white hover:${COLOR_SCALE.surface}/20`
    }`}
    aria-label={t('aria_extended', 'user_menu', 'User menu')}
    aria-expanded={isOpen}
  >
    <UserAvatar
      user={user}
      displayName={displayName}
      avatarInitial={avatarInitial}
      sizeClass="w-7 h-7"
      textSizeClass="text-xs"
    />
    <span className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.semibold} hidden sm:inline uppercase`}>
      {uppercaseDisplayName}
    </span>
    <ChevronDown
      className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    />
  </button>
);

interface ProfileSummaryProps {
  user: Tables<'profiles'> | null;
  displayName: string;
  uppercaseDisplayName: string;
  avatarInitial: string;
}

export const ProfileSummary = ({
  user,
  displayName,
  uppercaseDisplayName,
  avatarInitial,
}: ProfileSummaryProps) => (
  <div
    className={`flex items-center ${SPACE_X_SCALE['3']} ${SURFACE_PADDING['3']} ${PADDING_Y['3']} ${MARGIN_X['1']} ${MARGIN_BOTTOM['2']} ${RADIUS_SCALE.xl} bg-gradient-to-r from-gray-50/80 to-white border ${COLOR_SCALE['admin-border']} ${SHADOW_SCALE.sm}`}
  >
    <UserAvatar
      user={user}
      displayName={displayName}
      avatarInitial={avatarInitial}
      sizeClass="w-10 h-10"
      textSizeClass="text-sm"
    />
    <div className={`${SIZE_SCALE.minW0} ${FLEX.flex1}`}>
      <p
        className={`${TYPE_SCALE['2xs']} ${FONT_WEIGHTS.semibold} uppercase ${COLOR_SCALE.foreground} truncate`}
      >
        {uppercaseDisplayName}
      </p>
      {user?.email && (
        <p className={`${TYPE_SCALE['2xs']} ${COLOR_SCALE['foreground-muted']} truncate`}>
          {user.email}
        </p>
      )}
    </div>
  </div>
);

interface UserAvatarProps {
  user: Tables<'profiles'> | null;
  displayName: string;
  avatarInitial: string;
  sizeClass: string;
  textSizeClass: string;
}

export const UserAvatar = ({
  user,
  displayName,
  avatarInitial,
  sizeClass,
  textSizeClass,
}: UserAvatarProps) => {
  const hasProfileImage = Boolean(user?.profile_image_url);
  return (
    <>
      {hasProfileImage ? (
        <img
          src={user?.profile_image_url ?? undefined}
          alt={displayName}
          className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
          onError={e => {
            const imgElement = e.currentTarget as HTMLImageElement;
            imgElement.style.display = 'none';
            const nextSibling = imgElement.nextElementSibling as HTMLElement;
            nextSibling.style.display = 'flex';
          }}
          role="presentation"
        />
      ) : null}
      <div
        className={`${sizeClass} rounded-full bg-gradient-to-br from-gray-700 to-gray-500 flex items-center justify-center text-white ${textSizeClass} font-bold flex-shrink-0 ${
          hasProfileImage ? 'hidden' : 'flex'
        }`}
      >
        {avatarInitial}
      </div>
    </>
  );
};
